import { parseFilters, parsePagination, parseSorting } from '../query/query-utils'
import { OrmAdapter } from '../types/OrmAdapter'
import { Not } from 'typeorm'

// This adapter expects the consumer app to inject repositories via config.additionalSettings.repo
// It operates on a minimal repository interface: find, findOne, save, update, delete, count, createQueryBuilder

function resolveRepository(cfg: any): any {
  const hasRepoBasics = (r: any) => r && typeof r.find === 'function' && (typeof r.findAndCount === 'function' || typeof r.findOne === 'function')
  // 1) Prefer ctx.repository if present (trust the provided instance)
  const ctxRepo = (cfg as any)._ctx?.repository
  if (ctxRepo) return ctxRepo
  // 2) Evaluate additionalSettings.repo (function or object) and trust if provided
  let repo = cfg?.additionalSettings?.repo
  if (typeof repo === 'function') {
    try { repo = (repo as any)(cfg, (cfg as any)._ctx) } catch { repo = undefined }
  }
  if (repo) return repo
  // 3) Derive from any available DataSource
  const ctxDs = (cfg as any)._ctx?.dataSource
  let ds = ctxDs || ((cfg.service && (cfg.service as any).getDataSource?.()) || undefined)
  if (!ds) {
    try {
      const mr = (cfg as any)._ctx?.moduleRef
      if (mr) ds = mr.get('DataSource', { strict: false }) || mr.get((require('typeorm') as any).DataSource, { strict: false })
    } catch {}
  }
  const regDs = (require('../module/CrudmanRegistry') as any).CrudmanRegistry.get().getDataSource()
  const dataSource = ds || regDs
  if (dataSource) {
    try { repo = dataSource.getRepository(cfg.model) } catch (err) { throw err }
  }
  return repo
}

export const TypeormAdapter: OrmAdapter = {
  async list(req, cfg) {
    let repo = resolveRepository(cfg)
    if (!repo) {
      const dbg = {
        hasCtx: !!(cfg as any)._ctx,
        hasCtxRepo: !!((cfg as any)._ctx?.repository),
        hasAddRepo: !!(cfg?.additionalSettings?.repo),
        model: !!cfg?.model,
        hasCtxDs: !!((cfg as any)._ctx?.dataSource),
        hasRegDs: !!(require('../module/CrudmanRegistry').CrudmanRegistry.get().getDataSource())
      }
      throw new Error('RepoMissing-X ' + JSON.stringify(dbg))
    }
    const qn = cfg.queryParamNames || {}
    const pageNames = { page: qn.page || 'page', perPage: qn.perPage || 'perPage', paginate: qn.paginate || 'paginate' }
    const pagOpts = cfg.pagination || {}
    const { info: paginationInfo, skip, take, disabled: paginationDisabled } = parsePagination(req.query, pageNames, pagOpts)
    // If no whitelist provided, default to all entity columns (safe model-scoped default)
    const repoColumns: string[] = Array.isArray(repo?.metadata?.columns)
      ? repo.metadata.columns.map((c: any) => c.propertyName)
      : []
    const effectiveWhitelist: string[] = (cfg.filtersWhitelist && cfg.filtersWhitelist.length)
      ? cfg.filtersWhitelist
      : repoColumns
    const sortingRequested = parseSorting(req.query, effectiveWhitelist, qn.sortPrefix || 'sort.')
    // Default sort: createdAt DESC if present, else primary key DESC (fallback to id)
    const hasCreatedAt = repoColumns.includes('createdAt')
    const primaryField = Array.isArray((repo as any)?.metadata?.primaryColumns) && (repo as any).metadata.primaryColumns[0]
      ? (repo as any).metadata.primaryColumns[0].propertyName
      : 'id'
    const effectiveSorting = sortingRequested.length
      ? sortingRequested
      : (
        hasCreatedAt
          ? (
            primaryField && primaryField !== 'createdAt'
              ? [ { field: 'createdAt', order: 'DESC' as const }, { field: primaryField, order: 'DESC' as const } ]
              : [ { field: 'createdAt', order: 'DESC' as const } ]
          )
          : [ { field: primaryField, order: 'DESC' as const } ]
      )
    const { where, filters } = parseFilters(req.query, effectiveWhitelist, {
      minOp: qn.minOp || 'min',
      maxOp: qn.maxOp || 'max',
      gtOp: qn.gtOp || 'gt',
      ltOp: qn.ltOp || 'lt',
      betweenOp: qn.betweenOp || 'between',
      likeOp: qn.likeOp || 'like'
    })

    // Keyword search
    const keywordParam = (qn.keyword || cfg.keywordParamName || 'keyword')
    const kwRaw = (req.query ? req.query[keywordParam] : undefined) as string | undefined
    const kwCfg = cfg.keyword || {}
    const kwEnabled = kwCfg.isEnabled !== false
    const kwMin = kwCfg.minLength ?? 2
    const kwCaseSensitive = kwCfg.isCaseSensitive === true
    const kw = (typeof kwRaw === 'string' ? kwRaw.trim() : '')
    let keywordWhereOr: any[] = []
    let requiredRelations: string[] = []
    if (kwEnabled && kw && kw.length >= kwMin) {
      const paths: string[] = Array.isArray(kwCfg.searchableFields) && kwCfg.searchableFields.length
        ? kwCfg.searchableFields
        : repoColumns
      const maxDepth = kwCfg.maxRelationDepth ?? 1
      const likeValue = `%${kw}%`

      // Build OR conditions for each path
      for (const path of paths) {
        const segments = String(path).split('.')
        if (!segments.length || segments.length > 3) continue
        if (segments.length === 1) {
          const col = segments[0]
          if (!repoColumns.includes(col)) continue
          keywordWhereOr.push(buildLikeCondition('root', col, likeValue, kwCaseSensitive))
        } else {
          // nested: relation[.relation].column
          if (segments.length - 1 > maxDepth) continue
          const column = segments.pop() as string
          const relationPath = segments.join('.')
          requiredRelations.push(relationPath)
          keywordWhereOr.push(buildLikeCondition(relationPath, column, likeValue, kwCaseSensitive))
        }
      }
    }

    // Merge relations (existing + required by keyword nested paths)
    const relationsBaseRaw = cfg.getRelations ? await cfg.getRelations(req, null, cfg) : (cfg.relations || undefined)
    const relations = resolveRelations(repo, relationsBaseRaw, requiredRelations)

    // Combine where with keyword OR block
    let whereFinal: any = where
    if (keywordWhereOr.length) {
      // AND existing where with (OR keyword...) semantics; using a structure our helper understands later
      whereFinal = { AND: [ where || {}, { OR: keywordWhereOr } ] }
    }

    const hasLikeFilter = filters.some((f) => f.op === 'like')
    const hasRangeFilter = filters.some((f) => f.op === 'gte' || f.op === 'lte' || f.op === 'gt' || f.op === 'lt' || f.op === 'between')
    let findOptions: any = { where: convertToTypeormWhere(whereFinal), relations, order: toTypeormOrder(effectiveSorting), skip, take, select: normalizeSelect(cfg.attributes, repo) }
    if (cfg.onBeforeQuery) {
      const mod = await cfg.onBeforeQuery(findOptions, cfg.model, (cfg as any)._ctx, req, null)
      if (mod) findOptions = mod
    }
    let items: any[] = []
    let total = 0
    if (hasLikeFilter || hasRangeFilter) {
      // Use QueryBuilder for robust LIKE behavior across drivers
      const qb = repo.createQueryBuilder('t')
      // Ensure default relations are included (left join + select) when using QB path
      const relsForJoin: string[] = Array.isArray(relations) ? relations : []
      if (relsForJoin.length) joinRelationsIntoQueryBuilder(qb, relsForJoin)
      // Apply simple filters
      for (const f of filters) {
        if (f.op === 'like') {
          const param = `%${String(f.value)}%`.toLowerCase()
          qb.andWhere(`LOWER(t.${f.field}) LIKE :p_${f.field}`, { [`p_${f.field}`]: param })
        } else if (f.op === 'eq') {
          qb.andWhere(`t.${f.field} = :p_${f.field}`, { [`p_${f.field}`]: f.value })
        } else if (f.op === 'between' && Array.isArray(f.value)) {
          qb.andWhere(`t.${f.field} BETWEEN :p_${f.field}_a AND :p_${f.field}_b`, { [`p_${f.field}_a`]: f.value[0], [`p_${f.field}_b`]: f.value[1] })
        } else if (f.op === 'gte') {
          qb.andWhere(`t.${f.field} >= :p_${f.field}`, { [`p_${f.field}`]: f.value })
        } else if (f.op === 'lte') {
          qb.andWhere(`t.${f.field} <= :p_${f.field}`, { [`p_${f.field}`]: f.value })
        } else if (f.op === 'gt') {
          qb.andWhere(`t.${f.field} > :p_${f.field}`, { [`p_${f.field}`]: f.value })
        } else if (f.op === 'lt') {
          qb.andWhere(`t.${f.field} < :p_${f.field}`, { [`p_${f.field}`]: f.value })
        }
      }
      // Sorting (apply default when none specified)
      for (const s of effectiveSorting) {
        qb.addOrderBy(`t.${s.field}`, s.order as any)
      }
      // Pagination
      if (!paginationDisabled) {
        if (typeof skip === 'number') qb.skip(skip)
        if (typeof take === 'number') qb.take(take)
      }
      const resQB = await qb.getManyAndCount()
      items = resQB[0]; total = resQB[1]
      if (paginationDisabled) {
        paginationInfo.page = 1
        paginationInfo.perPage = items.length
        paginationInfo.totalItemsCount = total
        paginationInfo.totalPagesCount = 1
        paginationInfo.isHavingNextPage = false
        paginationInfo.isHavingPreviousPage = false
      } else {
        const totalPagesCount = take ? Math.ceil(total / take) : 0
        paginationInfo.totalItemsCount = total
        paginationInfo.totalPagesCount = totalPagesCount
        paginationInfo.isHavingNextPage = take ? paginationInfo.page < totalPagesCount : false
      }
    } else {
      if (paginationDisabled) {
        items = await repo.find({ where: findOptions.where, relations: findOptions.relations, order: findOptions.order, select: findOptions.select })
        total = items.length
        paginationInfo.page = 1
        paginationInfo.perPage = items.length
        paginationInfo.totalItemsCount = total
        paginationInfo.totalPagesCount = 1
        paginationInfo.isHavingNextPage = false
        paginationInfo.isHavingPreviousPage = false
      } else {
        const res = await repo.findAndCount(findOptions)
        items = res[0]; total = res[1]
        const totalPagesCount = take ? Math.ceil(total / take) : 0
        paginationInfo.totalItemsCount = total
        paginationInfo.totalPagesCount = totalPagesCount
        paginationInfo.isHavingNextPage = take ? paginationInfo.page < totalPagesCount : false
      }
    }
    if (cfg.onAfterFetch) {
      const mod = await cfg.onAfterFetch(items, req, (cfg as any)._ctx, null)
      if (mod) items = mod
    }

    return { items, pagination: paginationInfo, filters, sorting: effectiveSorting }
  },

  async details(req, cfg) {
    const repo = resolveRepository(cfg)
    if (!repo) throw new Error('Repository not provided (additionalSettings.repo or module dataSource needed)')
    const field = cfg.recordSelectionField || 'id'
    const value = (req.params && (req.params[field] ?? req.params.id ?? Object.values(req.params)[0]))
    const relationsBaseRaw = cfg.getRelations ? await cfg.getRelations(req, null, cfg) : (cfg.relations || undefined)
    const relations = resolveRelations(repo, relationsBaseRaw, [])
    let findOptions: any = { where: { [field]: castId(value) } as any, relations, select: normalizeSelect(cfg.attributes, repo) }
    if (cfg.onBeforeQuery) {
      const mod = await cfg.onBeforeQuery(findOptions, cfg.model, (cfg as any)._ctx, req, null)
      if (mod) findOptions = mod
    }
    let entity = await repo.findOne(findOptions)
    if (cfg.onAfterFetch && entity) {
      const mod = await cfg.onAfterFetch(entity, req, (cfg as any)._ctx, null)
      if (mod) entity = mod
    }
    return entity
  },

  async create(req, cfg) {
    const repo = resolveRepository(cfg)
    if (!repo) throw new Error('Repository not provided (additionalSettings.repo or module dataSource needed)')
    const input = this.normalizeInput(req.body, cfg.model)
    const entity = repo.create ? repo.create(input) : input
    return await repo.save(entity)
  },

  async update(req, cfg) {
    const repo = resolveRepository(cfg)
    if (!repo) throw new Error('Repository not provided (additionalSettings.repo or module dataSource needed)')
    const field = cfg.recordSelectionField || 'id'
    const rawParam = req.params && (req.params[field] ?? req.params.id ?? Object.values(req.params)[0])
    const value = castId(rawParam ?? req.body[field])
    const input = this.normalizeInput({ ...req.body }, cfg.model)
    const updateKeys = Object.keys(input).filter((k) => k !== field)
    if (updateKeys.length) {
      try {
        // Use QueryBuilder update with explicit where clause
        const primary = (repo as any)?.metadata?.primaryColumns?.[0]?.propertyName || field
        const qb = repo.createQueryBuilder()
        await qb.update(cfg.model).set(input as any).where(`${primary} = :id`, { id: value }).execute()
      } catch {
        try {
          // Prefer load-merge-save
          const existing = await repo.findOne({ where: { [field]: value } as any })
          if (existing) {
            for (const k of updateKeys) (existing as any)[k] = (input as any)[k]
            await repo.save(existing)
          } else {
            const toSave = repo.create({ [field]: value, ...input } as any)
            await repo.save(toSave)
          }
        } catch {
          await repo.update({ [field]: value } as any, input)
        }
      }
    }
    return await repo.findOne({ where: { [field]: value } as any })
  },

  async save(req, cfg) {
    const field = cfg.recordSelectionField || 'id'
    if (req.params[field] || req.body[field]) return this.update(req, cfg)
    return this.create(req, cfg)
  },

  async delete(req, cfg) {
    const repo = resolveRepository(cfg)
    if (!repo) throw new Error('Repository not provided (additionalSettings.repo or module dataSource needed)')
    const field = cfg.recordSelectionField || 'id'
    const rawParam = req.params && (req.params[field] ?? req.params.id ?? Object.values(req.params)[0])
    const value = castId(rawParam)
    await repo.delete({ [field]: value } as any)
  },

  async exists(where, cfg) {
    const repo = resolveRepository(cfg)
    if (!repo) throw new Error('Repository not provided (additionalSettings.repo or module dataSource needed)')
    const count = await repo.count({ where })
    return count > 0
  },

  async findOne(where, options, cfg) {
    const repo = resolveRepository(cfg)
    if (!repo) throw new Error('Repository not provided (additionalSettings.repo or module dataSource needed)')
    return await repo.findOne({ where, ...(options || {}) })
  },

  normalizeInput(input, _model) {
    const out: any = { ...input }
    for (const key in out) {
      const val = out[key]
      if (val instanceof Date) continue
      if (typeof val === 'string') {
        const d = new Date(val)
        if (!Number.isNaN(d.getTime()) && /\d{4}-\d{2}-\d{2}/.test(val)) out[key] = d
      }
    }
    return out
  },

  buildUniquenessWhere(id, fields, values, type) {
    let where: any
    
    if (fields.length === 1) {
      // Single field - return direct condition
      const field = fields[0]
      where = { [field]: values[field] }
    } else {
      // Multiple fields - use AND/OR logic
      if (type === 'and') {
        // AND: combine all fields in single object
        where = {}
        fields.forEach((f: string) => {
          where[f] = values[f]
        })
      } else {
        // OR: array of objects, each with one field
        where = fields.map((f: string) => ({ [f]: values[f] }))
      }
    }
    
    if (id !== undefined && id !== null) {
      // Exclude current record by primary key for update paths
      const primaryField = Array.isArray((this as any)?.metadata?.primaryColumns) && (this as any).metadata.primaryColumns[0]
        ? (this as any).metadata.primaryColumns[0].propertyName
        : 'id'
      
      // Add primary key condition to existing where
      if (Array.isArray(where)) {
        // OR case: add primary key condition to each clause
        where = where.map(clause => ({
          ...clause,
          [primaryField]: Not(id)
        }))
      } else {
        // AND case: add primary key condition to single object
        where[primaryField] = Not(id)
      }
    }
    console.log("Where is", JSON.stringify(where, null, 2))
    
    return where
  }
}

function toTypeormOrder(sorting: Array<{ field: string; order: 'ASC'|'DESC' }>): any {
  const order: any = {}
  for (const s of sorting) order[s.field] = s.order
  return order
}

function normalizeSelect(attrs: any, repo: any): any {
  if (!attrs || attrs === '*') return undefined
  if (Array.isArray(attrs)) return attrs
  if (attrs && typeof attrs === 'object') {
    const cols: string[] = Array.isArray(repo?.metadata?.columns)
      ? repo.metadata.columns.map((c: any) => c.propertyName)
      : []
    if (attrs.include && Array.isArray(attrs.include) && attrs.include.length) return attrs.include
    if (attrs.exclude && Array.isArray(attrs.exclude) && attrs.exclude.length) return cols.filter(c => !attrs.exclude.includes(c))
  }
  return undefined
}

function resolveRelations(repo: any, rels: any, required: string[]): string[] | undefined {
  const allRels: string[] = Array.isArray(repo?.metadata?.relations)
    ? repo.metadata.relations.map((r: any) => r.propertyName)
    : []
  let base: string[]
  if (!rels || rels === '*') {
    base = allRels
  } else if (Array.isArray(rels)) {
    base = rels
  } else if (typeof rels === 'object') {
    if (rels.include && Array.isArray(rels.include)) base = rels.include
    else if (rels.exclude && Array.isArray(rels.exclude)) base = allRels.filter((r) => !rels.exclude.includes(r))
    else base = allRels
  } else {
    base = []
  }
  const merged = Array.from(new Set([ ...base, ...(required || []) ]))
  return merged.length ? merged : undefined
}

function castId(v: any) { const n = Number(v); return Number.isNaN(n) ? v : n }

function buildLikeCondition(relationPath: string, column: string, likeValue: string, caseSensitive: boolean) {
  // We can't access QueryBuilder aliases in find options directly; use a pseudo expression format.
  // Consumer repos that support advanced where can map LOWER() semantics. Here we store intent.
  const target = relationPath === 'root' ? column : `${relationPath}.${column}`
  return caseSensitive
    ? { [target]: { like: likeValue } }
    : { [target]: { ilike: likeValue } } // signal case-insensitive intent
}

function joinRelationsIntoQueryBuilder(qb: any, relations: string[]) {
  const added: Set<string> = new Set()
  for (const path of relations) {
    const segments = String(path).split('.')
    let prevAlias = 't'
    let relPath = ''
    for (const seg of segments) {
      relPath = relPath ? `${relPath}.${seg}` : seg
      const alias = `t_${relPath.replace(/\./g, '_')}`
      const joinKey = `${prevAlias}.${seg}__${alias}`
      if (!added.has(joinKey)) {
        qb.leftJoinAndSelect(`${prevAlias}.${seg}`, alias)
        added.add(joinKey)
      }
      prevAlias = alias
    }
  }
}

// Convert our generic where structure into TypeORM FindOptionsWhere format
function convertToTypeormWhere(where: any): any {
  if (!where) return {}
  // Handle AND/OR composition
  if (where.AND) {
    return (where.AND as any[]).map(convertToTypeormWhere)
  }
  if (where.OR) {
    return (where.OR as any[]).map(convertToTypeormWhere)
  }
  const out: any = {}
  for (const key of Object.keys(where)) {
    const value = where[key]
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      if ('like' in value) {
        const raw = String((value as any).like)
        const pattern = raw.includes('%') ? raw : `%${raw}%`
        out[key] = CaseInsensitiveLike(pattern)
      } else if ('ilike' in value) {
        const raw = String((value as any).ilike)
        const pattern = raw.includes('%') ? raw : `%${raw}%`
        out[key] = CaseInsensitiveLike(pattern)
      } else if ('between' in value && Array.isArray((value as any).between)) {
        const [a, b] = (value as any).between
        out[key] = Between(a, b)
      } else if ('gte' in value || 'lte' in value || 'gt' in value || 'lt' in value) {
        const range: any[] = []
        if ('gte' in value && 'lte' in value) out[key] = Between((value as any).gte, (value as any).lte)
        else if ('gt' in value) out[key] = MoreThan((value as any).gt)
        else if ('gte' in value) out[key] = MoreThanOrEqual((value as any).gte)
        else if ('lt' in value) out[key] = LessThan((value as any).lt)
        else if ('lte' in value) out[key] = LessThanOrEqual((value as any).lte)
      } else {
        // nested relation path: split by dot
        if (key.includes('.')) {
          const [rel, ...rest] = key.split('.')
          const nestedKey = rest.join('.')
          out[rel] = out[rel] || {}
          out[rel][nestedKey] = convertToTypeormWhere({ [nestedKey]: value })[nestedKey]
        } else {
          out[key] = convertToTypeormWhere(value)
        }
      }
    } else {
      out[key] = value
    }
  }
  return out
}

// Helpers that defer importing from typeorm to runtime to avoid hard deps at import time
function Like(pattern: string): any { return dynamicTypeormOperator('Like', pattern) }
function Between(a: any, b: any): any { return dynamicTypeormOperator('Between', a, b) }
function MoreThan(v: any): any { return dynamicTypeormOperator('MoreThan', v) }
function MoreThanOrEqual(v: any): any { return dynamicTypeormOperator('MoreThanOrEqual', v) }
function LessThan(v: any): any { return dynamicTypeormOperator('LessThan', v) }
function LessThanOrEqual(v: any): any { return dynamicTypeormOperator('LessThanOrEqual', v) }
function Raw(...args: any[]): any { return dynamicTypeormOperator('Raw', ...args) }

// Case-insensitive like fallback: lower both sides via simple pattern; we map to Like on lowercased input
function CaseInsensitiveLike(pattern: string): any {
  // Use a LOWER() based Raw expression for cross-database case-insensitive matching
  return Raw((alias: string) => `LOWER(${alias}) LIKE LOWER(:pattern)`, { pattern })
}

function dynamicTypeormOperator(name: string, ...args: any[]): any {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const typeorm = require('typeorm')
    return (typeorm as any)[name](...args)
  } catch {
    // If typeorm isn't available at import time, just return a shape TypeORM understands minimally
    return { __op: name, __args: args }
  }
}


